import React, { PureComponent } from 'react';
import { format } from 'date-fns';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from '@client/shared/utils/routerUtils/routerUtils';
import { fromJS } from 'immutable';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { parseISO } from 'date-fns';
import { DRAFT_DATE_NEW_FUNDRAISER } from '@client/definitions/constants';
import FormWrapper from '@client/components/formComponents/formWrapper/FormWrapper';
import { formDataAppender } from '@client/shared/utils/utils/utils';
import IndividualFundraiser from '@client/containers/individualFundraiser/IndividualFundraiser';
import { getIn } from '@client/shared/utils/immutableUtils/immutableUtils';
import FormFundraiser from '@client/containers/formFundraiserContainer/formFundraiser/FormFundraiser';
import PreviewBox from '@client/containers/previewBox/PreviewBox';
import { putAndHandleFundraiser } from '@client/redux/fundraiser/fundraiserActions';
import { editFundraiserSchema } from '@client/shared/utils/validation/validation';
import { NotificationContext } from '@client/components/notificationProvider/NotificationProvider';
import { selectFundraiserTypes } from '@client/redux/fundraiser/fundraiserSelectors';
import {
  getDefaultDescriptionText,
  getFundraiserTypeId,
  isMemorialFundraiser,
} from '@client/shared/utils/fundraiserUtils/fundraiserUtils';
import { START_DATE, END_DATE, DATE_FORMAT } from '@client/definitions/api';
import { selectMyFundraisers } from '../../redux/user/userReducer';
import { loadMyFundraisers } from '../../redux/user/userActions';
import styles from './formFundraiserContainer.scss';

class FormFundraiserContainer extends PureComponent {
  static contextType = NotificationContext;

  constructor(props) {
    super(props);
    const initialFormValues = props.fundraiser;
    const startDate = getIn(initialFormValues, START_DATE);
    const endDate = getIn(initialFormValues, END_DATE);

    const endDateSetByUser =
      endDate &&
      format(parseISO(endDate), DATE_FORMAT) !== DRAFT_DATE_NEW_FUNDRAISER;

    const startDateSetByUser =
      startDate &&
      format(parseISO(startDate), DATE_FORMAT) !== DRAFT_DATE_NEW_FUNDRAISER;

    const dateSetByUser = endDateSetByUser || startDateSetByUser;

    const parsedStartDate =
      startDate && dateSetByUser ? parseISO(startDate) : new Date();
    const parsedEndDate = endDate && dateSetByUser ? parseISO(endDate) : null;

    this.state = {
      formValues: {
        title: getIn(initialFormValues, 'title'),
        description: getIn(initialFormValues, 'description'),
        image: getIn(initialFormValues, 'imageUrl'),
        targetAmount: getIn(initialFormValues, 'targetAmount'),
        fundraiserType: getIn(initialFormValues, 'fundraiserType'),
        eventName: getIn(initialFormValues, 'eventName'),
        memorialFirstName: getIn(initialFormValues, 'memorialFirstName'),
        memorialLastName: getIn(initialFormValues, 'memorialLastName'),
        memorialBirthDate: getIn(initialFormValues, 'memorialBirthDate'),
        memorialDeathDate: getIn(initialFormValues, 'memorialDeathDate'),
        startDate: parsedStartDate,
        endDate: parsedEndDate,
        acceptContentResponsibility: Boolean(
          getIn(initialFormValues, 'acceptContentResponsibility')
        ),
      },
      isMyFundraiser: false,
      isNew: false,
    };
  }

  componentDidMount() {
    const { location } = this.props;
    const isPreviewOrEditMode =
      location &&
      (location.search === '?edit=true' || location.search === '?preview=true');

    const isNew = location && location.search.includes('&new=true');

    this.setState((state) => {
      return { ...state, isMyFundraiser: !isPreviewOrEditMode, isNew: isNew };
    });

    if (isPreviewOrEditMode) {
      this.props.dispatch(loadMyFundraisers());
    }
  }

  componentDidUpdate() {
    if (this.state.isMyFundraiser) return;

    const creatorId = getIn(this.props.fundraiser, 'id');
    const userFundraisers = getIn(this.props.userFundraisers, 'results');

    if (userFundraisers) {
      const isMyFundraiser = userFundraisers.some(
        (fundraiser) => getIn(fundraiser, 'id') === creatorId
      );

      this.setState((state) => {
        return { ...state, isMyFundraiser };
      });
    }
  }

  handleSubmit = (values) => {
    const { history, fundraiser, dispatch } = this.props;

    const isPreview = values && values.target;
    const ifValues = isPreview ? this.state.formValues : values;
    const toIsoDate = (str) => {
      if (typeof str !== 'string') return str;
      const trimmed = str.trim();
      const m = trimmed.match(/^(\d{4})(\d{2})(\d{2})$/);
      if (m) return `${m[1]}-${m[2]}-${m[3]}`;
      return trimmed;
    };
    // Normalize memorial dates before sending
    ifValues.memorialBirthDate = toIsoDate(ifValues.memorialBirthDate);
    ifValues.memorialDeathDate = toIsoDate(ifValues.memorialDeathDate);
    const parsedStartDate = ifValues.startDate
      ? format(ifValues.startDate, DATE_FORMAT)
      : null;
    const parsedEndDate = ifValues.endDate
      ? format(ifValues.endDate, DATE_FORMAT)
      : null;

    const creatorId = getIn(fundraiser, 'creator', 'id');

    const formData = new FormData();
    formData.append('creatorId', creatorId);
    if (ifValues.image === null) {
      formDataAppender(formData, 'deleteImage', true);
    } else if (ifValues.image instanceof Blob) {
      formDataAppender(formData, 'image', ifValues.image, `${creatorId}.jpeg`);
    }
    formDataAppender(formData, 'title', ifValues.title);
    formDataAppender(formData, 'description', ifValues.description);
    formDataAppender(formData, 'targetAmount', ifValues.targetAmount);
    formDataAppender(formData, START_DATE, parsedStartDate);
    formDataAppender(formData, END_DATE, parsedEndDate);

    formDataAppender(
      formData,
      'fundraiserType',
      getFundraiserTypeId(ifValues.fundraiserType)
    );

    if (isMemorialFundraiser(ifValues.fundraiserType)) {
      formDataAppender(
        formData,
        'memorialFirstName',
        ifValues.memorialFirstName
      );
      formDataAppender(formData, 'memorialLastName', ifValues.memorialLastName);
      formDataAppender(
        formData,
        'memorialBirthDate',
        ifValues.memorialBirthDate
      );
      formDataAppender(
        formData,
        'memorialDeathDate',
        ifValues.memorialDeathDate
      );
    }

    formDataAppender(
      formData,
      'acceptContentResponsibility',
      ifValues.acceptContentResponsibility
    );

    const fundraiserId = getIn(fundraiser, 'id');
    const path =
      ifValues.fundraiserType === 'MemorialFund'
        ? `/minnesfonder/${fundraiserId}`
        : `/insamling/${fundraiserId}`;

    dispatch(putAndHandleFundraiser(formData, fundraiserId)).then(() => {
      history.push({
        pathname: path,
        search: '',
      });
    });
  };

  togglePreview = (formValues) => {
    const isPreview = formValues && formValues.target;
    const ifValues = isPreview ? this.state.formValues : formValues;

    const previewValues = {
      image: ifValues.image,
      title: ifValues.title,
      description: ifValues.description,
      targetAmount: parseInt(ifValues.targetAmount),
      fundraiserType: ifValues.fundraiserType,
      memorialFirstName: ifValues.memorialFirstName,
      memorialLastName: ifValues.memorialLastName,
      memorialBirthDate: ifValues.memorialBirthDate,
      memorialDeathDate: ifValues.memorialDeathDate,
      startDate: ifValues.startDate,
      endDate: ifValues.endDate,
      acceptContentResponsibility: ifValues.acceptContentResponsibility,
    };

    const isValid = editFundraiserSchema().isValidSync(previewValues);

    if (isValid) {
      if (isPreview) {
        this.props.history.goBack();
      } else {
        this.props.history.push({
          search: '?preview=true',
        });
        window.scrollTo(0, 0);
      }

      this.context.closeNotification();
      this.setState(() => {
        return {
          formValues: previewValues,
        };
      });
    } else {
      this.context.displayNotification(
        'ERROR',
        `Kontrollera att alla fält är korrekt ifyllda.`,
        5000
      );
    }
  };

  renderForm = (formProps) => {
    const type = getIn(this.props.fundraiser, 'fundraiserType');
    const defaultDescriptionText = getDefaultDescriptionText(
      this.props.fundraiserTypes,
      type
    );

    return (
      <FormFundraiser
        formProps={formProps}
        location={location}
        onTogglePreview={this.togglePreview}
        translations={this.props.pageData}
        defaultDescriptionText={defaultDescriptionText}
      />
    );
  };

  render() {
    // If not user's fundraiser or logged out user, return here early.
    if (!this.state.isMyFundraiser) return null;

    const { className, location, translations } = this.props;
    const { formValues } = this.state;
    const isPreviewMode = location.search === '?preview=true';

    return (
      <>
        {isPreviewMode && (
          <PreviewBox
            handleSubmit={this.handleSubmit}
            togglePreview={this.togglePreview}
            isNew={this.state.isNew}
          />
        )}
        <div
          className={`${styles.formFundraiserContainer}${
            className ? ` ${className}` : ''
          }`}
        >
          {isPreviewMode && formValues ? (
            <IndividualFundraiser
              disableClick={true}
              fundraiser={fromJS({
                title: formValues.title,
                description: formValues.description,
                imageUrl:
                  formValues.image && typeof formValues.image === 'object'
                    ? URL.createObjectURL(formValues.image)
                    : formValues.image,
                creator: getIn(this.props.fundraiser, 'creator'),
                id: getIn(this.props.fundraiser, 'id'),
                targetAmount: formValues.targetAmount,
                fundraiserType: getIn(this.props.fundraiser, 'fundraiserType'),
                memorialFirstName: formValues.memorialFirstName,
                memorialLastName: formValues.memorialLastName,
                memorialBirthDate: formValues.memorialBirthDate,
                memorialDeathDate: formValues.memorialDeathDate,
              })}
              translations={translations}
            />
          ) : (
            <FormWrapper
              initialValues={formValues}
              onSubmit={this.handleSubmit}
              component={this.renderForm}
              validateOnBlur={true}
              validateOnChange={true}
              validationSchema={editFundraiserSchema()}
            />
          )}
        </div>
      </>
    );
  }
}

FormFundraiserContainer.propTypes = {
  className: PropTypes.string,
  fundraiser: PropTypes.object,
  dispatch: PropTypes.func,
  history: PropTypes.object,
  location: PropTypes.object,
  pageData: ImmutablePropTypes.map,
  fundraiserTypes: ImmutablePropTypes.list,
  userFundraisers: ImmutablePropTypes.map,
  translations: ImmutablePropTypes.map,
};

function mapStateToProps(state) {
  return {
    fundraiserTypes: selectFundraiserTypes(state),
    userFundraisers: selectMyFundraisers(state),
  };
}

export default withRouter(connect(mapStateToProps)(FormFundraiserContainer));
